import _ from 'lodash';
import { EVENTS_MAP } from '@wix/blocks-widget-services/common';
import {
  getPrimaryConnectionItem,
  isRefHost,
} from '../../services/StructureBuilder/structureUtil';
import type { Behavior } from '@wix/yoshi-flow-editor';

const findConnectionByControllerId = (
  controllerId: any,
  connectionItems: any,
) => _.find(connectionItems, { controllerId }) || {};
const getConnectionByControllerId = (controllerId: any, compDef: any) => {
  if (isRefHost(compDef)) {
    return findConnectionInDataOverrides(controllerId, compDef);
  }
  const connections = compDef.connections;
  return findConnectionByControllerId(
    controllerId,
    connections ? connections.items : undefined,
  );
};
const findConnectionInDataOverrides = (controllerId: any, compDef: any) => {
  if (!compDef) {
    return undefined;
  }
  const customData = compDef.custom || {};
  const { overriddenData } = customData;
  if (_.isEmpty(overriddenData)) {
    return undefined;
  }
  const connectionsItem = _.find(overriddenData, { itemType: 'connections' });
  return findConnectionByControllerId(
    controllerId,
    connectionsItem.dataItem.items,
  );
};
const getWixCodeConnectionItem = ({ connections }: any) =>
  _.find(connections.items, { type: 'WixCodeConnectionItem' });

export const getFindNicknameConnectionFu = (masterStructure: any) => {
  const controllerDataId = _.get(masterStructure, 'data.id');
  return _.partial(getConnectionByControllerId, controllerDataId);
};
const getExistingConnectionsMap = async (editorSDK: any, widgetRef: any) => {
  const currentConnections =
    await editorSDK.document.controllers.listControllerConnections('token', {
      controllerRef: widgetRef,
    });
  return _.keyBy(currentConnections, 'connection.role');
};
const isAppWidget = ({ componentType }: any) =>
  componentType === 'platform.components.AppWidget';
const getModesDefinitionsAndVisibility = (
  component: any,
  role: any,
  parentsMap: any,
  descriptorMap: any,
) => {
  let modeDefinitions;
  let modesVisibility;
  if (component.modes) {
    if (!_.isEmpty(component.modes.definitions)) {
      modeDefinitions = _(component.modes.definitions)
        .keyBy('modeId')
        .mapValues('type')
        .value();
    }
    const visibleByDefault = !component.modes.isHiddenByModes;
    const ancestorModes = getAncestorModesIfExists(
      parentsMap[role],
      parentsMap,
      descriptorMap,
    );
    if (!_.isEmpty(ancestorModes)) {
      modesVisibility = _.mapValues(ancestorModes, () => visibleByDefault);
      _.forEach(component.modes.overrides, (override: any) => {
        const visibleInMode = !override.isHiddenByModes;
        _.forEach(override.modeIds, (modeId: any) => {
          modesVisibility[modeId] = visibleInMode;
        });
      });
    }
  }
  return {
    modeDefinitions,
    modesVisibility,
  };
};
const getAncestorModesContainerAndModeIdReplaceMap = async (
  editorSDK: any,
  controllerRef: any,
  componentRole: any,
  compDescriptor: any,
  componentsDescriptorsMap: any,
) => {
  if (isAffectedByModes(compDescriptor)) {
    const ancestorModesContainerDescriptor =
      getAncestorModesContainerDescriptor(
        componentRole,
        componentsDescriptorsMap,
      );
    if (ancestorModesContainerDescriptor) {
      const modesContainerRef = await getCompRefFromRole(
        editorSDK,
        controllerRef,
        ancestorModesContainerDescriptor.role,
      );
      if (!modesContainerRef) {
        return {};
      }
      const modes = await editorSDK.components.modes.getModes('token', {
        componentRef: modesContainerRef,
      });
      if (modesContainerRef && !_.isEmpty(modes)) {
        const modesReplaceMap = buildModeReplaceMap(
          ancestorModesContainerDescriptor.modeDefinitions,
          modes,
        );
        return {
          modesContainerRef,
          modesReplaceMap,
        };
      }
    }
  }
  return {};
};
const getBehaviorsItemsWithFixedModeIds = (
  compStructure: any,
  modesReplaceMap: any,
) => {
  const behaviors = compStructure.behaviors;
  if (behaviors.items) {
    const parsedItems = JSON.parse(behaviors.items);
    parsedItems.forEach((item: any) => {
      if (item.params && item.params.modeIds) {
        item.params.modeIds = item.params.modeIds.map(
          (id: any) => modesReplaceMap[id],
        );
      }
    });
    return JSON.stringify(parsedItems);
  }
};
const isWixCodeBehaviorItem = (behaviorItem: any) =>
  _.includes(['runCode', 'runAppCode'], _.get(behaviorItem, 'behavior.name'));
const updateOverriddenConnectionItemsControllerId = (
  connectionItems: any,
  controllerDataId: any,
  originalControllerDataId: any,
) =>
  connectionItems
    .filter(
      (connectionItem: any) =>
        connectionItem.controllerId === originalControllerDataId,
    )
    .forEach((connectionItem: any) => {
      connectionItem.controllerId = controllerDataId;
    });
const updateConnectionOverrides = (
  overriddenData: any,
  controllerDataId: any,
  originalControllerDataId: any,
) => {
  overriddenData
    .filter((overrideItem: any) => overrideItem.itemType === 'connections')
    .forEach((overrideItem: any) => {
      updateOverriddenConnectionItemsControllerId(
        overrideItem.dataItem.items,
        controllerDataId,
        originalControllerDataId,
      );
    });
};
const isMediaBoxRoot = ({ componentType }: any) =>
  componentType === 'wysiwyg.viewer.components.MediaBox';
const getStructureAndBehaviors = (
  originalStructure: any,
  controllerDataId: any,
  originalControllerDataId: any,
) => {
  const behaviors: any = [];
  const componentDefinition = _.cloneDeep(originalStructure);
  const widgetStructureRoot = getWidgetStructureRoot(componentDefinition);
  if (_.get(componentDefinition, 'layout.docked')) {
    delete componentDefinition.layout.docked;
  }
  if (_.get(componentDefinition, 'mobileStructure.layout.docked')) {
    delete componentDefinition.mobileStructure.layout.docked;
  }
  if (_.get(componentDefinition, 'mobileStructure.layout')) {
    componentDefinition.mobileStructure.layout.x = 0;
    componentDefinition.mobileStructure.layout.y = 0;
  }
  if (isMediaBoxRoot(widgetStructureRoot)) {
    widgetStructureRoot.props = _.assign({}, widgetStructureRoot.props, {
      type: 'MediaBoxProperties',
      allowOverflowContent: true,
    });
    widgetStructureRoot.style =
      widgetStructureRoot.style === 'mc1' ? 'mb1' : widgetStructureRoot.style;
  }
  const createConnectionItem = ({ role }: any) => ({
    role,
    controllerId: controllerDataId,
    type: 'ConnectionItem',
    isPrimary: true,
  });
  const setCompsConnectionsAndBehaviors = (comp: any) => {
    let wixCodeConnectionItem;
    if (_.get(comp, 'components')) {
      comp.components.forEach(setCompsConnectionsAndBehaviors);
    }
    let role: any;
    if (_.get(comp, 'connections')) {
      wixCodeConnectionItem = getWixCodeConnectionItem(comp);
      if (originalControllerDataId) {
        role = getConnectionByControllerId(originalControllerDataId, comp).role;
        comp.connections.items = fixCompConnections(
          comp.connections.items,
          originalControllerDataId,
          controllerDataId,
        );
        if (wixCodeConnectionItem) {
          comp.connections.items = _.reject(
            comp.connections.items,
            wixCodeConnectionItem,
          );
        }
      } else if (wixCodeConnectionItem) {
        role = wixCodeConnectionItem.role;
        comp.connections.items = _.reject(
          comp.connections.items,
          wixCodeConnectionItem,
          // @ts-expect-error
        ).concat(createConnectionItem(wixCodeConnectionItem));
      }
    }
    if (_.get(comp, 'behaviors')) {
      const [codeItems, otherItems] = _.partition(
        JSON.parse(comp.behaviors.items),
        isWixCodeBehaviorItem,
      );
      behaviors.push(
        ...codeItems.map(
          ({
            action,
            behavior,
          }: {
            action: { name: keyof typeof EVENTS_MAP };
            behavior: {
              params: Behavior;
            };
          }) => ({
            role,
            event: EVENTS_MAP[action.name],
            callbackId: behavior.params.callbackId,
          }),
        ),
      );
      if (otherItems.length) {
        comp.behaviors.items = JSON.stringify(otherItems);
      } else {
        delete comp.behaviors;
      }
    }
    if (isRefHost(comp)) {
      const dataOverrides = _.get(comp, 'custom.overriddenData');
      updateConnectionOverrides(
        dataOverrides,
        controllerDataId,
        originalControllerDataId,
      );
    }
  };
  setCompsConnectionsAndBehaviors(componentDefinition);
  return {
    behaviors,
    componentDefinition,
  };
};
const constrainLayoutToContainer = (
  componentDefinition: any,
  containerLayout: any,
) => {
  const compLayout = componentDefinition.layout;
  if (
    compLayout.x + compLayout.width > containerLayout.width ||
    compLayout.y + compLayout.height > containerLayout.height
  ) {
    const constrainedCompLayout = _.assign({}, compLayout, { x: 0, y: 0 });
    return _.assign({}, componentDefinition, { layout: constrainedCompLayout });
  }
  return componentDefinition;
};
const getCompRefFromRole = async (
  editorSDK: any,
  componentRef: any,
  role: any,
) => {
  const isRoleExists = async (compRef: any) => {
    const connections =
      await editorSDK.document.controllers.listControllerConnections('token', {
        controllerRef: compRef,
      });
    const primaryConnectionItem = getPrimaryConnectionItem(connections);
    return primaryConnectionItem && primaryConnectionItem.role === role;
  };
  if (await isRoleExists(componentRef)) {
    return componentRef;
  }
  const childrenRefs = await editorSDK.components.getChildren('token', {
    componentRef,
  });
  return _.find(childrenRefs, (childRef: any) => isRoleExists(childRef));
};
const getAncestorModesIfExists = (
  role: any,
  parents: any,
  descriptorMap: any,
): any => {
  if (!role) {
    return null;
  }
  if (descriptorMap[role].modeDefinitions) {
    return descriptorMap[role].modeDefinitions;
  }
  return getAncestorModesIfExists(parents[role], parents, descriptorMap);
};
const getAncestorModesContainerDescriptor = (
  role: any,
  componentsMetaDataMap: any,
) => {
  const visibleModes = getVisibleModes(
    componentsMetaDataMap[role].modesVisibility,
  );
  const modeIdInOverride = _.head(visibleModes);
  if (!modeIdInOverride) {
    return null;
  }
  let currComp = componentsMetaDataMap[componentsMetaDataMap[role].parent];
  while (currComp) {
    const modeDefinitions = currComp.modeDefinitions;
    if (modeDefinitions && modeDefinitions[modeIdInOverride]) {
      return currComp;
    }
    currComp = componentsMetaDataMap[currComp.parent];
  }
  return null;
};
const getWidgetStructureRoot = (componentDefinition: any) => {
  if (isAppWidget(componentDefinition)) {
    return _.head(componentDefinition.components);
  }
  return componentDefinition;
};
const fixCompConnections = (
  connectionItems: any,
  originalControllerDataId: any,
  controllerDataId: any,
) =>
  _.map(connectionItems, (item: any) => {
    if (item.controllerId === originalControllerDataId) {
      item.controllerId = controllerDataId;
    }
    return item;
  });
export const getMissingComponentDefinitions = async (
  editorSDK: any,
  masterStructure: any,
  widgetRef: any,
) => {
  const existingConnectionsMap = await getExistingConnectionsMap(
    editorSDK,
    widgetRef,
  );
  const getConnection = getFindNicknameConnectionFu(masterStructure);
  return reduceByKey(
    masterStructure,
    'components',
    (removedComps: any, compDef: any, i: any) => {
      const role = getConnection(compDef).role;
      const isRoot = i === 0;
      if (isRoot || isAppWidget(compDef) || existingConnectionsMap[role]) {
        return removedComps;
      }
      return [...removedComps, compDef];
    },
    [],
  );
};
const reduceByKey = (
  root: Record<string, any> = {},
  key: any,
  iteratee: any,
  accumulator = {},
) => {
  const items = [...(root[key] || [])];
  let i = 0;
  while (items.length > 0) {
    const currentItem = items.shift();
    items.push(...(currentItem[key] || []));
    accumulator = iteratee(accumulator, currentItem, i++);
  }
  return accumulator;
};
const buildComponentsDescriptorMap = (widgetStructure: any) => {
  const parentsMap: any = {};
  const getConnection = getFindNicknameConnectionFu(widgetStructure);
  return reduceByKey(
    widgetStructure,
    'components',
    (descriptorMap: any, component: any) => {
      const { role } = getConnection(component);
      const modesStructure = getModesDefinitionsAndVisibility(
        component,
        role,
        parentsMap,
        descriptorMap,
      );
      descriptorMap[role] = _.assign(
        {
          role,
          parent: parentsMap[role] || null,
        },
        modesStructure,
      );
      _.forEach(component.components, (childComp: any) => {
        const childRole = getConnection(childComp).role;
        parentsMap[childRole] = role;
      });
      return descriptorMap;
    },
  );
};
const getAddableComponents = async (
  editorSDK: any,
  controllerRef: any,
  masterStructure: any,
) => {
  if (!masterStructure) {
    return [];
  }
  const removedStructures = await getMissingComponentDefinitions(
    editorSDK,
    masterStructure,
    controllerRef,
  );
  const getConnection = getFindNicknameConnectionFu(masterStructure);
  return (removedStructures as any).map(
    (structure: any) => getConnection(structure).role,
  );
};
const getOriginalControllerDataId = (masterStructure: any) => {
  if (!masterStructure || !isAppWidget(masterStructure)) {
    return undefined;
  }
  const data = masterStructure.data || {};
  return data.id;
};
const isAffectedByModes = (compDescriptor: any) =>
  !_.isEmpty(compDescriptor.modesVisibility);
const getVisibleModes = (modesVisibility: any) =>
  _(modesVisibility).pickBy().keys().value();
const buildModeReplaceMap = (
  oldModeDefinitions: any,
  newModeDefinitions: any,
) =>
  _.mapValues(oldModeDefinitions, (modeType: any) => {
    const modeDefinition = _.find(newModeDefinitions, { type: modeType });
    return modeDefinition && modeDefinition.modeId;
  });
const getModeOverridesWithFixedIds = (overrides: any, modesReplaceMap: any) =>
  _.map(overrides, (override: any) =>
    _.assign({}, override, {
      modeIds: _.map(override.modeIds, (id: any) => modesReplaceMap[id]),
    }),
  );
const fixModeOverridesAndBehaviors = async (
  editorSDK: any,
  compStructure: any,
  controllerRef: any,
  componentRole: any,
  containerRef: any,
  componentsDescriptorsMap: any,
) => {
  let shouldApplyToAllModes = false;
  const compDescriptor = componentsDescriptorsMap[componentRole];
  const { modesContainerRef, modesReplaceMap } =
    await getAncestorModesContainerAndModeIdReplaceMap(
      editorSDK,
      controllerRef,
      componentRole,
      compDescriptor,
      componentsDescriptorsMap,
    );
  if (modesContainerRef && modesReplaceMap) {
    compStructure.behaviors.items = getBehaviorsItemsWithFixedModeIds(
      compStructure,
      modesReplaceMap,
    );
    compStructure.modes.overrides = getModeOverridesWithFixedIds(
      compStructure.modes.overrides,
      modesReplaceMap,
    );
    const visibleModes = getVisibleModes(compDescriptor.modesVisibility);
    shouldApplyToAllModes = visibleModes.length > 1;
    const firstVisibleModeId = modesReplaceMap[_.head(visibleModes)!];
    return {
      preAction: async () => {
        if (modesContainerRef && firstVisibleModeId) {
          await editorSDK.document.components.modes.activateComponentMode(
            'token',
            {
              componentRef: modesContainerRef,
              modeId: firstVisibleModeId,
            },
          );
        }
      },
      compStructure,
      postAction: async (addedCompRef: any) => {
        if (shouldApplyToAllModes) {
          await editorSDK.document.components.modes.applyCurrentToAllModes({
            componentRef: addedCompRef,
          });
        }
      },
    };
  }
  return {
    preAction: _.noop,
    compStructure,
    postAction: _.noop,
  };
};
const fixConnectedComponentDefinition = async (
  originalControllerDataId: any,
  editorSDK: any,
  componentStructure: any,
  controllerRef: any,
) => {
  const { id: controllerDataId } = await editorSDK.components.data.get(
    'token',
    { componentRef: controllerRef },
  );
  const { componentDefinition } = getStructureAndBehaviors(
    componentStructure,
    controllerDataId,
    originalControllerDataId,
  );
  return {
    preAction: _.noop,
    compStructure: componentDefinition,
    postAction: _.noop,
  };
};
const fixLayout = async (
  editorSDK: any,
  compStructure: any,
  controllerRef: any,
  componentRole: any,
  containerRef: any,
) => {
  const containerLayout = await editorSDK.components.layout.get('token', {
    componentRef: containerRef,
  });
  return {
    preAction: _.noop,
    compStructure: constrainLayoutToContainer(compStructure, containerLayout),
    postAction: _.noop,
  };
};
const getStructureAndParentByRole = async (
  editorSDK: any,
  role: any,
  controllerRef: any,
  widgetStructure: any,
  container = null,
  getConnections: any,
): Promise<any> => {
  if (widgetStructure) {
    if (getConnections(widgetStructure).role === role) {
      const containerRef = await getCompRefFromRole(
        editorSDK,
        controllerRef,
        getConnections(container).role,
      );
      return { compStructure: widgetStructure, containerRef };
    }
    if (widgetStructure.components) {
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < widgetStructure.components.length; i++) {
        const result = await getStructureAndParentByRole(
          editorSDK,
          role,
          controllerRef,
          widgetStructure.components[i],
          widgetStructure,
          getConnections,
        );
        if (result) {
          return result;
        }
      }
    }
  }
  return null;
};
const runAddComponentPlugins = async (
  editorSDK: any,
  plugins: any,
  origCompStructure: any,
  controllerRef: any,
  componentRole: any,
  containerRef: any,
  componentsDescriptorsMap: any,
) => {
  const preAddActions = [];
  const postAddActions = [];
  let compStructure = _.cloneDeep(origCompStructure);
  for (const plugin of plugins) {
    const {
      preAction,
      postAction,
      compStructure: fixedStructure,
    } = await plugin(
      editorSDK,
      compStructure,
      controllerRef,
      componentRole,
      containerRef,
      componentsDescriptorsMap,
    );
    compStructure = fixedStructure;
    preAddActions.push(preAction);
    postAddActions.push(postAction);
  }
  return {
    preAddActions,
    compStructure,
    postAddActions,
  };
};
const runActions = async (actionsArray: any, ...args: any[]) => {
  for (const action of actionsArray) {
    await action(...args);
  }
};
export const addComponentToOpenWidget = async (
  editorSDK: any,
  controllerRef: any,
  componentRole: any,
  masterStructure: any,
) => {
  const addableComponents = await getAddableComponents(
    editorSDK,
    controllerRef,
    masterStructure,
  );
  const componentsDescriptorsMap =
    buildComponentsDescriptorMap(masterStructure);
  const masterControllerDataId = getOriginalControllerDataId(masterStructure);
  const structurePreparationsPlugins = [
    fixModeOverridesAndBehaviors,
    _.partial(fixConnectedComponentDefinition, masterControllerDataId),
    fixLayout,
  ];
  if (_.includes(addableComponents, componentRole)) {
    const { compStructure: origCompStructure, containerRef } =
      await getStructureAndParentByRole(
        editorSDK,
        componentRole,
        controllerRef,
        masterStructure,
        null,
        getFindNicknameConnectionFu(masterStructure),
      );
    const { preAddActions, compStructure, postAddActions } =
      await runAddComponentPlugins(
        editorSDK,
        structurePreparationsPlugins,
        origCompStructure,
        controllerRef,
        componentRole,
        containerRef,
        componentsDescriptorsMap,
      );
    await runActions(preAddActions);
    const addedCompRef = await editorSDK.components.add('token', {
      pageRef: containerRef,
      componentDefinition: compStructure,
    });
    await runActions(postAddActions, addedCompRef);
    await editorSDK.document.history.add('token', { label: 'Element added' });
  }
};
export const addAllComponentsToOpenWidget = async (
  editorSDK: any,
  controllerRef: any,
  masterStructure: any,
) => {
  const componentsRole = await getAddableComponents(
    editorSDK,
    controllerRef,
    masterStructure,
  );
  return Promise.all(
    componentsRole.map((role: any) =>
      addComponentToOpenWidget(editorSDK, controllerRef, role, masterStructure),
    ),
  );
};
